/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-10
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCrudService, API_CONFIG, HttpService } from '@nebula-shared/data-access';
import type { CriterioOrden } from '../../../select-dialog/orden-dialogo';

export interface ArticuloDto {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  idTipoArticulo?: number;
  nombreTipoArticulo?: string;
  idUnidadMedida?: number;
  nombreUnidadMedida?: string;
  precio?: number;
  costo?: number;
  stock?: number;
  /** Codigo del estado (`ACTIVO`/`INACTIVO`) tal como lo manda el servidor (Mantis #352). */
  estadoArticulo?: string;
  clientId?: number;
  companyId?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  version?: number;
}

export interface PageResponseArticulo {
  content: ArticuloDto[];
  size: number;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class ArticuloService extends BaseCrudService<any, any, any> {
  readonly httpService = inject(HttpService);

  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.articulos || '/articulos'}`);
  }

  /**
   * Obtener artículos paginados - SIN filtro de estado automático
   */
  getArticulosPage(
    page = 0,
    size = 20,
    searchTerm?: string,
    filters?: { tipoArticuloId?: number },
    ordersBy?: CriterioOrden[],
  ): Observable<PageResponseArticulo> {
    const searchsBy: any[] = [];

    if (filters?.tipoArticuloId) {
      searchsBy.push({
        key: 'tipoArticuloId',
        operation: 'EQUAL',
        value: filters.tipoArticuloId,
      });
    }

    if (searchTerm) {
      searchsBy.push({
        key: 'codigo',
        operation: 'MATCH',
        value: searchTerm,
      });
    }

    const body = {
      page: page,
      size: size,
      searchsBy: searchsBy,
      ordersBy: ordersBy?.length ? ordersBy : [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        return this.mapToPageResponse(response);
      }),
    );
  }

  /**
   * Buscar un artículo específico por código
   */
  getArticuloByCodigo(codigo: string): Observable<ArticuloDto | null> {
    const body = {
      page: 0,
      size: 10,
      searchsBy: [
        {
          key: 'codigo',
          operation: 'EQUAL',
          value: codigo,
        },
      ],
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.length > 0 ? content[0] : null;
      }),
    );
  }

  private extractContent(response: any): any[] {
    if (response?.response?.content?.content && Array.isArray(response.response.content.content)) {
      return response.response.content.content;
    }

    if (response?.response?.content && Array.isArray(response.response.content)) {
      return response.response.content;
    }

    if (response?.content?.content && Array.isArray(response.content.content)) {
      return response.content.content;
    }

    if (response?.content && Array.isArray(response.content)) {
      return response.content;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  }

  private extractPageMetadata(response: any): {
    size: number;
    number: number;
    numberOfElements: number;
    totalElements: number;
    totalPages: number;
  } {
    if (response?.response?.content && typeof response.response.content === 'object') {
      const metadata = response.response.content;
      return {
        size: metadata.size || 0,
        number: metadata.number || 0,
        numberOfElements: metadata.numberOfElements || 0,
        totalElements: metadata.totalElements || 0,
        totalPages: metadata.totalPages || 0,
      };
    }

    if (
      response?.content &&
      typeof response.content === 'object' &&
      !Array.isArray(response.content)
    ) {
      return {
        size: response.content.size || 0,
        number: response.content.number || 0,
        numberOfElements: response.content.numberOfElements || 0,
        totalElements: response.content.totalElements || 0,
        totalPages: response.content.totalPages || 0,
      };
    }

    return {
      size: response.size || 0,
      number: response.number || 0,
      numberOfElements: response.numberOfElements || 0,
      totalElements: response.totalElements || 0,
      totalPages: response.totalPages || 0,
    };
  }

  private mapToPageResponse(response: any): PageResponseArticulo {
    const content = this.extractContent(response);
    const metadata = this.extractPageMetadata(response);

    return {
      content: content,
      size: metadata.size,
      number: metadata.number,
      numberOfElements: metadata.numberOfElements,
      totalElements: metadata.totalElements,
      totalPages: metadata.totalPages,
      empty: content.length === 0,
    };
  }
}
